import { useMemo } from 'react'
import { useCurrentFrame, useVideoConfig } from 'remotion'

// Type definitions
interface Word {
  word: string
  startFrame: number
  endFrame: number
  wordIndex: number
  isCurrentWord?: boolean
}

interface Animation {
  highlightStyle?: (baseStyle: any, word: any, frame: number, fps: number) => any
  highlightBoxStyle?: (baseStyle: any, word: any, frame: number, fps: number) => any
  highlightStyleBefore?: (baseStyle: any, word: any, frame: number, fps: number, highlightStyle: any) => any
  highlightStyleAfter?: (baseStyle: any, word: any, frame: number, fps: number, highlightStyle: any) => any
  highlightFill?: (word: any, frame: number, fps: number) => number
  highlightTextStyle?: (word: any, frame: number, fps: number) => any
}

interface SubtitleWordProps {
  word: Word
  animation: Animation
  highlightStyle: any
  highlightBoxStyle: any
}

const DEFAULT_STYLE_ANIMATION = (baseStyle: any/* , word, frame, fps */) => baseStyle

const clamp01 = (value: number) => Math.min(1, Math.max(0, value))

export default function SubtitleWord({
  word,
  animation: animationFromProps,
  highlightStyle,
  highlightBoxStyle,
}: SubtitleWordProps) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()

  const isWordBefore = word.endFrame <= frame

  const animation = useMemo(() => ({
    highlightStyle: DEFAULT_STYLE_ANIMATION,
    highlightBoxStyle: DEFAULT_STYLE_ANIMATION,
    highlightStyleBefore: DEFAULT_STYLE_ANIMATION,
    highlightStyleAfter: DEFAULT_STYLE_ANIMATION,
    ...animationFromProps,
  }), [animationFromProps])

  const style = useMemo(() => {
    if (word.isCurrentWord) {
      return animation.highlightStyle(highlightStyle, word, frame, fps)
    }

    if (isWordBefore) {
      return animation.highlightStyleBefore({}, word, frame, fps, highlightStyle)
    }

    return animation.highlightStyleAfter({}, word, frame, fps, highlightStyle)
  }, [
    word,
    highlightStyle,
    animation,
    isWordBefore,
    frame,
    fps,
  ])

  // Karaoke fill: the word keeps the line color and a highlight-colored copy
  // on top is revealed left to right. Needs a highlight color to paint with.
  const fillProgress = word.isCurrentWord && animation.highlightFill && style?.color
    ? clamp01(animation.highlightFill(word, frame, fps))
    : null
  const { color: fillColor, ...unfilledStyle } = style ?? {}
  const textStyle = word.isCurrentWord && animation.highlightTextStyle
    ? animation.highlightTextStyle(word, frame, fps)
    : null
  // Transcript words often carry a leading space; the fill and the text
  // decoration cover the spoken text only. Elements (not bare whitespace text)
  // keep those spaces rendered inside the inline-flex word.
  const [, leading = '', text = word.word, trailing = ''] = (
    fillProgress !== null || textStyle ? /^(\s*)([\s\S]*?)(\s*)$/.exec(word.word) ?? [] : []
  )

  return (
    <div
      key={word.wordIndex}
      style={{
        position: 'relative',
        display: 'inline-flex',
        whiteSpace: 'pre',
        ...(fillProgress === null ? style : unfilledStyle),
      }}
    >
      {word.isCurrentWord && highlightBoxStyle && (
        <div
          style={highlightBoxStyle}
        >
          {word.word}
        </div>
      )}
      {fillProgress === null && !textStyle ? word.word : (
        <>
          {leading && <span>{leading}</span>}
          <span
            data-testid="subtitle-word-text"
            style={{ position: 'relative', ...textStyle }}
          >
            {text}
            {fillProgress !== null && (
              <span
                aria-hidden="true"
                data-testid="subtitle-word-fill"
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'pre',
                  color: fillColor,
                  clipPath: `inset(0% ${Number(((1 - fillProgress) * 100).toFixed(2))}% 0% 0%)`,
                  zIndex: 100,
                  pointerEvents: 'none',
                }}
              >
                {text}
              </span>
            )}
          </span>
          {trailing && <span>{trailing}</span>}
        </>
      )}
    </div>
  )
}
